import { useObserver } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components/macro';
import { CartItemProps } from '../types/CartItemProps';
import { TypeProduct } from '../types/TypeProduct';
import {
  formatCurrency,
  formatCurrencyWithoutDecimals,
} from '../utils/formatCurrencyWithoutDecimals';
import { useFallbackImage } from '../utils/useFallbackImage';
import Button from './Button';
import NumericStepper from './NumericStepper';
import Tickbox from './Tickbox';

const UnstyledCartItem: React.FC<CartItemProps> = ({
  className,
  id,
  image,
  name,
  vintage,
  price,
  value,
  step,
  activeLabel,
  warning,
  onDecrement,
  onIncrement,
  onToggleOption,
  stockAvailable,
  singleItemDescriptor,
  pluralItemDescriptor,
  variationTypeDescription,
  removeFromCart,
  productOptions,
  selectedOptions,
  calculatingState,
  currencyCode,
}) => {
  if (!stockAvailable) {
    warning = (warning ? warning + ', ' : '') + 'Insufficient stock';
  }

  const fallbackImage = useFallbackImage(image);

  return useObserver(() => (
    <div className={'ww-cart-item ' + className}>
      <div className="image">
        <img onError={fallbackImage} src={image} alt={name} />
      </div>
      <div className="details">
        <div className="header">
          <div className="product">
            <h3>
              {name}{' '}
              {vintage &&
                vintage !== '0' &&
                name.indexOf(vintage) < 0 &&
                vintage}
            </h3>
          </div>
          <div
            className={`price${
              calculatingState ? ' ww-cart-item-total-' + calculatingState : ''
            }`}
          >
            <h3>{value > 0 && `$${formatCurrencyWithoutDecimals(price)}`}</h3>
          </div>
        </div>
        <div className="footer">
          <div className="footer-left">
            {value > 0 && (
              <>
                <div
                  className={`priceInfo${
                    calculatingState
                      ? ' ww-cart-item-price-' + calculatingState
                      : ''
                  }`}
                >
                  $
                  {formatCurrencyWithoutDecimals(price / (value / (step ?? 1)))}
                  {activeLabel?.suffixText ??
                    ((step ?? 0) > 1 ? ' per ' + step : '')}
                  {variationTypeDescription
                    ? ' - ' + variationTypeDescription
                    : ''}
                </div>
              </>
            )}
            {activeLabel && (
              <div
                className={`priceDiscount${
                  calculatingState
                    ? ' ww-cart-item-price-discount-' + calculatingState
                    : ''
                }`}
              >
                {activeLabel.discountText}
              </div>
            )}
          </div>
          <div className="footer-right">
            {warning && <span className="warning">{warning}</span>}
            <Button
              title="Remove from cart"
              centeredContent
              transparentBorder
              onClick={() => removeFromCart()}
            >
              ×
            </Button>
            <NumericStepper
              value={value}
              step={step}
              onIncrement={onIncrement}
              onDecrement={onDecrement}
              singleItemDescriptor={singleItemDescriptor}
              pluralItemDescriptor={pluralItemDescriptor}
            />
          </div>
        </div>
        {productOptions && productOptions.length > 0 && (
          <div className="ww-cart-item-options">
            {productOptions
              .filter(
                (o) =>
                  o.optionProduct === null ||
                  ((o.optionProduct as TypeProduct).isSaleableWithStock &&
                    (o.optionProduct as TypeProduct).minimumUnitPrice !== null),
              )
              .map((o) => (
                <div key={o.id}>
                  <Tickbox
                    key={o.id}
                    name={'optionTickbox-' + id + '-' + o.id}
                    value={o.optionProductId ?? o.name ?? ''}
                    active={
                      selectedOptions.filter(
                        (s) =>
                          (o.name && s.name === o.name) ||
                          (o.optionProductId &&
                            s.productId === o.optionProductId),
                      ).length > 0
                    }
                    circular={false}
                    onTick={() => {
                      onToggleOption(
                        o,
                        selectedOptions.filter(
                          (s) =>
                            (o.name && s.name === o.name) ||
                            (o.optionProductId &&
                              s.productId === o.optionProductId),
                        ).length === 0,
                      );
                    }}
                  >
                    <span className="ww-cart-item-option-name">
                      {o.optionProduct?.name ?? o.name}
                      {o.optionProduct &&
                        o.optionProduct.vintage &&
                        o.optionProduct.vintage !== '0' &&
                        o.optionProduct.name.indexOf(o.optionProduct.vintage) <
                          0 &&
                        o.optionProduct.vintage}
                    </span>
                    <span className="ww-cart-item-option-unit-price">
                      {o.optionProduct &&
                      (o.optionProduct as TypeProduct).minimumUnitPrice
                        ? '+' +
                          formatCurrency(
                            (o.optionProduct as TypeProduct).minimumUnitPrice,
                            currencyCode,
                            { hideZeroDecimals: true },
                          )
                        : ''}
                    </span>
                  </Tickbox>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  ));
};

const CartItem = styled(UnstyledCartItem)`
  display: flex;
  flex-direction: row;
  background-color: var(--white);
  padding: 0.25em;

  .warning {
    margin: 0.25em 1em;
    font-size: 0.8em;
    text-align: right;
    color: var(--warn-color);
  }

  .image {
    flex: none;
    padding: 0.25em;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 3.6em;
    overflow: hidden;
  }

  .image img {
    height: auto;
    width: auto;
  }

  .details {
    display: flex;
    flex-direction: column;
    flex: auto;
    padding: 0.25em 0.25em 0.25em 0;
  }

  .header {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    flex: 0 0 auto;
  }

  .product {
    display: flex;
    flex-direction: column;
    flex: auto;
  }

  .product h3 {
    margin: 0.25em 0 0.5em 0;
    font-size: 1.17em;
    text-align: left;
  }

  .product .ww-product-labels {
    font-size: 0.9em;
    margin-bottom: 0.5em;
  }

  .price {
    margin-left: 1em;
    flex: none;
    padding: 0.25em 0.5em;
  }

  .priceInfo {
    color: var(--secondary-color);
    font-weight: bold;
  }

  .priceDiscount {
    color: var(--gray-6, #888888);
  }

  .price h3 {
    margin: 0;
    font-size: 1.17em;
  }

  .footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-end;
  }

  .footer-right {
    flex-grow: 1;
    display: flex;
    justify-content: flex-end;
    align-items: stretch;
  }

  .footer .ww-numeric-stepper {
    margin-left: 0.5em;
  }

  .ww-cart-item-options > div {
    margin-bottom: 0.5em;
    display: flex;
  }

  .ww-cart-item-options > div > * {
    flex-shrink: 1;
  }

  .ww-cart-item-option-unit-price {
    padding-left: 0.5em;
  }

  .ww-cart-item-price-idle,
  .ww-cart-item-total-idle,
  .ww-cart-item-price-discount-idle {
    transition: opacity 300ms ease-out;
    opacity: 1;
  }

  .ww-cart-item-price-pending,
  .ww-cart-item-total-pending,
  .ww-cart-item-total-pending > h3 {
    color: var(--gray-1);
    background-color: var(--gray-1);
  }

  .ww-cart-item-price-discount-pending {
    opacity: 0;
  }

  @media (min-width: 569px) {
    padding: 0.5em;

    .image {
      padding: 0.5em 0.5em 0.5em 0;
      width: 6.923em;
    }

    .footer > span {
      font-size: 1em;
    }

    .footer .ww-numeric-stepper {
      margin-left: 1em;
    }
  }
`;

export default CartItem;
